import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { History, Check, X, MinusCircle } from "lucide-react";
import { StatusPill, winnerStatus, bonusStatus } from "@/components/StatusPill";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime, STATUS_LABEL } from "@/lib/format";
import { signed } from "@/lib/share";
import { EmptyState, ErrorBox, PageHeader, Spinner, TeamMark } from "@/components/Bits";
import AdvancedQuestions from "@/components/AdvancedQuestions";

const OUTCOME = {
  won: ["Gagnée", "text-[#B7F52A] bg-[#B7F52A]/10 border-[#B7F52A]/30"],
  lost: ["Manquée", "text-slate-400 bg-white/5 border-white/10"],
  cancelled: ["Annulée", "text-red-300 bg-red-400/10 border-red-400/20"],
  pending: ["En attente", "text-slate-200 bg-white/5 border-white/10"],
};
const FILTERS = [["all", "Toutes"], ["pending", "En attente"], ["won", "Gagnées"], ["lost", "Manquées"]];

const answerState = (q) => {
  if (q.status === "void") return [MinusCircle, "text-slate-400", ""];
  if (q.status === "resolved" && q.my_answer) return (q.correct_options || []).includes(q.my_answer) ? [Check, "text-[#B7F52A]", "Bonne réponse"] : [X, "text-red-300", "Mauvaise réponse"];
  return [null, "", ""];
};

function Points({ p }) {
  const qs = (p.questions || []).filter((q) => q.status !== "void" && q.my_answer);
  if (p.breakdown) {
    const b = p.breakdown;
    return (
      <p className="text-xs" data-testid={`prediction-points-${p.id}`}>
        Points obtenus : <b className={b.total >= 0 ? "text-[#B7F52A]" : "text-red-300"}>{signed(b.total)}</b>
        <span className="text-slate-500"> (vainqueur <b className={b.winner > 0 ? "text-[#B7F52A]" : b.winner < 0 ? "text-red-300" : ""}>{signed(b.winner)}</b> · bonus <b className={b.questions > 0 ? "text-[#B7F52A]" : b.questions < 0 ? "text-red-300" : ""}>{signed(b.questions)}</b>{b.streak ? <> · série <b className="text-[#B7F52A]">{signed(b.streak)}</b></> : ""})</span>
      </p>
    );
  }
  if (p.status === "final") return <p className="text-xs text-amber-200" data-testid={`prediction-total-waiting-${p.id}`}>Total : en attente de validation officielle</p>;
  if (!p.rule || p.status === "cancelled") return null;
  const gain = p.rule.correct_points + qs.reduce((s, q) => s + q.points_win, 0);
  const risk = p.rule.wrong_points + qs.reduce((s, q) => s + q.points_lose, 0);
  return <p className="text-xs text-slate-300" data-testid={`prediction-potential-${p.id}`}>Potentiel : <b className="text-[#B7F52A]">{signed(gain)}</b> · Risque : <b className="text-red-300">{signed(risk)}</b></p>;
}

function Row({ p, tz, setItems }) {
  const qs = p.questions || [];
  return (
    <article className="space-y-2 px-4 py-4" data-testid={`prediction-row-${p.id}`}>
      <div className="flex items-start justify-between gap-3">
        <Link to={`/matchs/${p.id}`} className="flex min-w-0 items-center gap-2 hover:opacity-90" data-testid={`prediction-link-${p.id}`}>
          <TeamMark team={p.away} /> <span className="text-xs text-slate-500">@</span> <TeamMark team={p.home} />
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold">{p.away.name} @ {p.home.name}</span>
            <span className="block text-xs text-slate-400">{fmtDateTime(p.start_time, tz)} · {STATUS_LABEL[p.status]}{p.status === "final" && p.home_score != null ? ` · ${p.away_score}–${p.home_score}` : ""}</span>
          </span>
        </Link>
        <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-bold ${OUTCOME[p.outcome][1]}`}>{OUTCOME[p.outcome][0]}</span>
      </div>
      <p className="flex flex-wrap items-center gap-1.5 text-xs text-slate-300">Vainqueur choisi : <b className="text-white">{p[p.my_pick].name}</b>
        {p.rule && <span className="text-slate-500">({signed(p.rule.correct_points)} / {signed(p.rule.wrong_points)})</span>}
        <StatusPill s={winnerStatus(p)} testId={`prediction-winner-status-${p.id}`} /></p>
      {qs.length > 0 && (
        <div className="space-y-1" data-testid={`prediction-bonus-${p.id}`}>
          <p className="text-xs text-slate-400">Questions bonus : <b className="text-white">{qs.filter((q) => q.my_answer).length}/{qs.length}</b> répondues</p>
          {qs.map((q) => {
            const [Icon, cls, label] = answerState(q);
            const opt = q.options.find((o) => o.key === q.my_answer);
            return (
              <p key={q.id} className="flex flex-wrap items-center gap-1 text-[11px] text-slate-400">
                {q.label} : <span className="text-slate-200">{opt ? opt.label : "—"}</span>
                <StatusPill s={bonusStatus(q, p)} testId={`prediction-bonus-status-${q.id}`} />
                {label && <span className={`flex items-center gap-0.5 ${cls}`}>{Icon && <Icon size={11} />} {label}</span>}
                <span className="text-slate-500">({signed(q.points_win)} / {signed(q.points_lose)})</span>
              </p>
            );
          })}
        </div>
      )}
      <Points p={p} />
      {!p.locked && qs.length > 0 && <AdvancedQuestions game={p} setGames={setItems} />}
    </article>
  );
}

export default function MyPredictions() {
  const { tz, currentLeague } = useAuth();
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    let active = true;
    api.get("/me/predictions", { params: currentLeague?.id ? { league_id: currentLeague.id } : {} })
      .then(({ data }) => { if (active) setItems(data); }).catch((e) => { if (active) setError(errMsg(e)); });
    return () => { active = false; };
  }, [currentLeague?.id]);
  const shown = (items || []).filter((i) => filter === "all" || i.outcome === filter);
  return (
    <div>
      <PageHeader eyebrow={currentLeague ? `Historique · ${currentLeague.name}` : "Historique personnel"} title="Mes prédictions" />
      <div className="mpl-scroll-x mb-4 flex gap-2 overflow-x-auto">
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`predictions-filter-${k}`} aria-pressed={filter === k}
            className={`shrink-0 rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${filter === k ? "border-[#B7F52A] bg-[#B7F52A] text-[#05070D]" : "border-white/10 text-slate-300 hover:bg-white/5"}`}>{l}</button>
        ))}
      </div>
      {error && <ErrorBox message={error} />}
      {!items && !error && <Spinner />}
      {items && (shown.length ? (
        <div className="mpl-card divide-y divide-white/[0.05]" data-testid="predictions-list">
          {shown.map((p) => <Row key={p.id} p={p} tz={tz} setItems={setItems} />)}
        </div>
      ) : (
        <EmptyState icon={History} title="Aucune prédiction" text="Faites vos choix dans les matchs NBA." cta="Voir les matchs" to="/matchs" testId="predictions-empty" />
      ))}
    </div>
  );
}
